// src/components/ProductCard.js
import React, { useState, useEffect } from 'react';
import { getProduct } from '../db';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ImageCarousel from './ImageCarousel';
import useSiteData from './useSiteData';
import { productSlideSeconds } from '../photos';
import { useTheme } from '@mui/material/styles';
import { addToCart } from '../products';

/**
 * One product on the storefront. `position` is where it sits in the grid,
 * which staggers the cards' photo slideshows into a wave.
 */
const ProductCard = ({ product, position = 0 }) => {
  // How long each photo shows, as set in the dashboard. Unknown until the
  // site's settings are read, and the cards hold still until then.
  const siteData = useSiteData();
  const slideSeconds = siteData ? productSlideSeconds(siteData) : 0;
  const [quantity, setQuantity] = useState(1);
  const [available, setAvailable] = useState(null);
  const [message, setMessage] = useState("");
  const [needsCode, setNeedsCode] = useState(false);
  const [forSale, setForSale] = useState(product.isForSale !== false);
  const [showcase, setShowcase] = useState(Boolean(product.showcase));
  const [customDescription, setCustomDescription] = useState(product.description || '');
  const [customShowcaseDescription, setCustomShowcaseDescription] = useState(
    product.showcaseDescription || ''
  );
  const [customShowcaseDescMode, setCustomShowcaseDescMode] = useState(
    product.showcaseDescMode || 'tied'
  );

  useEffect(() => {
    fetchStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStock = async () => {
    try {
      const data = await getProduct(product.id);
      if (data) {
        // Oversold stock sits below zero; to a customer that is simply none.
        setAvailable(Math.max(0, Number(data.available) || 0));
        setNeedsCode(data.needsCode || false);
        setForSale(data.isForSale !== false);
        setShowcase(data.showcase === true);
        if (data.description !== undefined) setCustomDescription(data.description);
        if (data.showcaseDescription !== undefined) {
          setCustomShowcaseDescription(data.showcaseDescription);
        }
        if (data.showcaseDescMode !== undefined) {
          setCustomShowcaseDescMode(data.showcaseDescMode);
        }
      }
    } catch (err) {
      setMessage(`Error fetching stock: ${err.message}`);
    }
  };

  const incrementQuantity = () => {
    if (available !== null && quantity < available) {
      setQuantity(quantity + 1);
    } else {
      toast.warning("Reached maximum available stock.");
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const isShowcase = !forSale && (showcase || Boolean(product.showcase));
  const baseDescription = customDescription || product.description || '';
  const showcaseText = (customShowcaseDescription || product.showcaseDescription || '').trim();
  const showcaseMode = customShowcaseDescMode || product.showcaseDescMode || 'tied';

  let displayDescription = baseDescription;
  let showcaseNotice = null;

  if (isShowcase) {
    if (showcaseMode === 'replace' && showcaseText) {
      displayDescription = showcaseText;
    } else if (showcaseMode === 'both' && showcaseText) {
      displayDescription = baseDescription;
      showcaseNotice = showcaseText;
    } else {
      displayDescription = baseDescription;
    }
  }

  const handleAddToCart = () => {
    if (!forSale || isShowcase) {
      toast.error("This item is not currently for sale.");
      return;
    }
    if (available === null) {
      toast.error("Stock data not loaded yet. Please try again.");
      return;
    }
    if (available === 0) {
      toast.error("Sorry, this item is currently out of stock.");
      return;
    }
    // Never more in the cart than can be sold.
    const { trimmed } = addToCart(product, quantity, available);
    if (trimmed) {
      toast.warning(`Adjusted quantity to available stock: ${available}`);
    }
    toast.success(`Added ${product.name} to your cart!`);
    if (needsCode) {
      toast.info('A valid code will be required at checkout for this item');
    }
  };

  const theme = useTheme();

  // Everything below is presentation. The stock rules that decide these labels
  // are the same ones the handlers above enforce.
  const outOfStock = available === 0;
  const unavailable = outOfStock || !forSale || isShowcase;

  const stockBadge = () => {
    if (isShowcase) return { label: 'Showcase only', tone: 'neutral' };
    if (!forSale) return { label: 'Not for sale', tone: 'error' };
    if (available === null) return null;
    if (outOfStock) return { label: 'Out of stock', tone: 'error' };
    return { label: `${available} in stock`, tone: 'neutral' };
  };

  const badge = stockBadge();

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'transform .25s ease, box-shadow .25s ease, border-color .25s ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          borderColor:
            theme.palette.mode === 'dark'
              ? 'rgba(156, 184, 95, 0.45)'
              : 'rgba(85, 107, 47, 0.35)',
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 14px 34px rgba(0, 0, 0, 0.5)'
              : '0 14px 34px rgba(60, 54, 40, 0.12)',
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <ImageCarousel
          photos={product.photos}
          height={230}
          alt={product.name}
          // The card's width in the home page grid: three across the 1200px
          // container from lg up, two from sm, one on a phone — less the
          // container's padding and the grid's gutters, measured, so the
          // browser does not reach for a wider copy than the card can show.
          sizes="(min-width: 1200px) 368px, (min-width: 600px) calc(50vw - 36px), calc(100vw - 32px)"
          // Each card's photos move on by themselves, the cards one after
          // another in a wave rather than all at the same moment.
          autoPlaySeconds={slideSeconds || null}
          autoPlayDelayMs={(position % 6) * 800}
        />

        {badge && (
          <Chip
            size="small"
            label={badge.label}
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.02em',
              color: badge.tone === 'error' ? '#fff' : '#f4f1e6',
              backgroundColor:
                badge.tone === 'error' ? 'rgba(178, 34, 34, 0.88)' : 'rgba(20, 22, 15, 0.68)',
              backdropFilter: 'blur(4px)',
            }}
          />
        )}

        {needsCode && (
          <Chip
            size="small"
            label="Code required"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 700,
              color: '#1a1d14',
              backgroundColor: 'rgba(232, 191, 92, 0.94)',
              backdropFilter: 'blur(4px)',
            }}
          />
        )}
      </Box>

      <CardContent
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 2.5,
          '&:last-child': { pb: 2.5 },
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, lineHeight: 1.3, color: 'text.primary' }}
        >
          {product.name}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            mt: 0.75,
            color: 'text.secondary',
            lineHeight: 1.5,
            minHeight: 44,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
          title={displayDescription}
        >
          {displayDescription}
        </Typography>

        {showcaseNotice && (
          <Box
            sx={{
              mt: 1,
              px: 1.25,
              py: 0.6,
              borderRadius: 1.5,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(156, 184, 95, 0.12)'
                  : 'rgba(85, 107, 47, 0.08)',
              border: '1px solid',
              borderColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(156, 184, 95, 0.25)'
                  : 'rgba(85, 107, 47, 0.2)',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.mode === 'dark' ? '#c5db94' : 'primary.dark',
                fontWeight: 600,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.35,
              }}
              title={showcaseNotice}
            >
              {showcaseNotice}
            </Typography>
          </Box>
        )}

        {needsCode && (
          <Typography variant="body2" sx={{ mt: 1.5, color: 'warning.main', fontWeight: 600 }}>
            Requires delivery code to purchase
          </Typography>
        )}

        {message && (
          <Typography variant="body2" sx={{ mt: 1.5, color: 'error.main' }}>
            {message}
          </Typography>
        )}

        {/* Pushes the price and the buttons to the bottom, so cards of
            different description lengths still line up across the row. */}
        <Box sx={{ flexGrow: 1, minHeight: 16 }} />

        <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mb: 2 }}>
          {isShowcase && (!product.price || Number(product.price) <= 0) ? (
            <Typography
              sx={{ fontWeight: 600, fontSize: '1.15rem', color: 'text.secondary', lineHeight: 1.2 }}
            >
              Price on enquiry
            </Typography>
          ) : (
            <>
              <Typography
                sx={{ fontWeight: 700, fontSize: '1.5rem', color: 'primary.main', lineHeight: 1.1 }}
              >
                R{Number(product.price || 0).toFixed(2)}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>each</Typography>
            </>
          )}
        </Stack>

        {isShowcase ? (
          <Button
            variant="outlined"
            fullWidth
            disabled
            sx={{
              py: 1.1,
              borderRadius: 999,
              fontWeight: 700,
              textTransform: 'none',
              fontSize: '0.95rem',
            }}
          >
            Showcase Only
          </Button>
        ) : (
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                borderRadius: 999,
                border: '1px solid',
                borderColor: 'divider',
                px: 0.5,
                flexShrink: 0,
              }}
            >
              <IconButton
                size="small"
                onClick={decrementQuantity}
                disabled={quantity <= 1}
                aria-label={`Remove one ${product.name}`}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
              <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 700 }}>
                {quantity}
              </Typography>
              <IconButton
                size="small"
                onClick={incrementQuantity}
                disabled={available !== null && quantity >= available}
                aria-label={`Add one ${product.name}`}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              disableElevation
              onClick={handleAddToCart}
              disabled={unavailable}
              sx={{
                py: 1.1,
                borderRadius: 999,
                fontWeight: 700,
                textTransform: 'none',
                fontSize: '0.95rem',
              }}
            >
              {/* Off sale comes first, as on the badge above the price. */}
              {!forSale ? 'Not for Sale' : outOfStock ? 'Out of Stock' : 'Add to Cart'}
            </Button>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductCard;
